import type { StorageAdapter, MutationEnvelope } from '@edv-next/sync-engine';

/**
 * Web IndexedDB adapter — pure IndexedDB (no Dexie hard dep). Works in any
 * browser that provides `indexedDB`. Object stores:
 *   - `outbox`         keyPath=mutation_id, indexed by site_id
 *   - `entities`       keyPath=[site_id, entity_type, entity_id]
 *   - `checkpoints`    keyPath=site_id
 *   - `conflicts`      keyPath=id (auto)
 *   - `notifications`  keyPath=id (auto)
 */

export interface IndexedDbAdapterConfig {
  dbName: string;
  schemaVersion: number;
  factory?: IDBFactory;
}

const SCHEMA_MIN = 1;

async function openDb(cfg: IndexedDbAdapterConfig): Promise<IDBDatabase> {
  const factory = cfg.factory ?? (globalThis.indexedDB as IDBFactory | undefined);
  if (!factory) throw new Error('indexedDB not available');
  return new Promise((resolve, reject) => {
    const req = factory.open(cfg.dbName, cfg.schemaVersion ?? SCHEMA_MIN);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('outbox')) {
        const s = db.createObjectStore('outbox', { keyPath: 'mutation_id' });
        s.createIndex('by_site', 'site_id', { unique: false });
        s.createIndex('by_next_attempt', 'next_attempt_at', { unique: false });
      }
      if (!db.objectStoreNames.contains('entities')) {
        db.createObjectStore('entities', { keyPath: ['site_id', 'entity_type', 'entity_id'] });
      }
      if (!db.objectStoreNames.contains('checkpoints')) {
        db.createObjectStore('checkpoints', { keyPath: 'site_id' });
      }
      if (!db.objectStoreNames.contains('conflicts')) {
        db.createObjectStore('conflicts', { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains('notifications')) {
        db.createObjectStore('notifications', { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
  });
}
function txn(
  db: IDBDatabase,
  stores: string[],
  mode: IDBTransactionMode = 'readonly',
): IDBTransaction {
  return db.transaction(stores, mode);
}
function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((res, rej) => {
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

export function createIndexedDbStorage(cfg: IndexedDbAdapterConfig): StorageAdapter {
  let dbP: Promise<IDBDatabase> | null = null;
  const db = () => (dbP ??= openDb(cfg));
  return {
    async enqueue(m: MutationEnvelope) {
      const d = await db();
      const t = txn(d, ['outbox'], 'readwrite');
      await req(t.objectStore('outbox').put(m as unknown as Record<string, unknown>));
      await new Promise<void>((res, rej) => {
        t.oncomplete = () => res();
        t.onerror = () => rej(t.error);
      });
    },
    async dequeue(id: string) {
      const d = await db();
      const t = txn(d, ['outbox'], 'readwrite');
      await req(t.objectStore('outbox').delete(id));
      await new Promise<void>((res, rej) => {
        t.oncomplete = () => res();
        t.onerror = () => rej(t.error);
      });
    },
    async updateMutation(id: string, patch: Record<string, unknown>) {
      const d = await db();
      const t = txn(d, ['outbox'], 'readwrite');
      const s = t.objectStore('outbox');
      const cur = (await req(s.get(id))) as Record<string, unknown> | undefined;
      if (cur) await req(s.put({ ...cur, ...patch }));
      await new Promise<void>((res, rej) => {
        t.oncomplete = () => res();
        t.onerror = () => rej(t.error);
      });
    },
    async outbox() {
      const d = await db();
      const t = txn(d, ['outbox'], 'readonly');
      const arr = (await req(t.objectStore('outbox').getAll())) as MutationEnvelope[];
      return arr;
    },
    async getCheckpoint(site_id: string) {
      const d = await db();
      const t = txn(d, ['checkpoints'], 'readonly');
      const row = (await req(t.objectStore('checkpoints').get(site_id))) as
        | { last_seq?: number }
        | undefined;
      return row?.last_seq ?? 0;
    },
    async setCheckpoint(site_id: string, seq: number) {
      const d = await db();
      const t = txn(d, ['checkpoints'], 'readwrite');
      await req(t.objectStore('checkpoints').put({ site_id, last_seq: seq }));
      await new Promise<void>((res, rej) => {
        t.oncomplete = () => res();
        t.onerror = () => rej(t.error);
      });
    },
    async applyPageAndAdvance(site_id, snapshots, next_after) {
      const d = await db();
      const t = txn(d, ['entities', 'checkpoints'], 'readwrite');
      const eS = t.objectStore('entities');
      for (const raw of snapshots) {
        const s = raw as Record<string, unknown>;
        const entity_type = String(s.entity_type ?? '');
        const entity_id = String(s.entity_id ?? '');
        if (!entity_type || !entity_id) continue;
        const snap = s.snapshot as Record<string, unknown> | null;
        if (snap == null || s.op === 'delete' || s.op === 'archive') {
          await req(eS.delete([site_id, entity_type, entity_id] as unknown as IDBValidKey));
        } else {
          await req(eS.put({ site_id, entity_type, entity_id, ...snap }));
        }
      }
      await req(t.objectStore('checkpoints').put({ site_id, last_seq: next_after }));
      await new Promise<void>((res, rej) => {
        t.oncomplete = () => res();
        t.onerror = () => rej(t.error);
      });
    },
    async recordConflict(envelope, snapshot) {
      const d = await db();
      const t = txn(d, ['conflicts'], 'readwrite');
      await req(t.objectStore('conflicts').add({ envelope, snapshot, at: Date.now() } as never));
      await new Promise<void>((res, rej) => {
        t.oncomplete = () => res();
        t.onerror = () => rej(t.error);
      });
    },
    async purgeSite(site_id, opts = {}) {
      const d = await db();
      const t = txn(
        d,
        ['outbox', 'entities', 'checkpoints', 'conflicts', 'notifications'],
        'readwrite',
      );
      // outbox
      const oxIdx = t.objectStore('outbox').index('by_site');
      const cur = oxIdx.openCursor(IDBKeyRange.only(site_id));
      await new Promise<void>((res, rej) => {
        cur.onsuccess = async () => {
          const c = cur.result;
          if (!c) {
            res();
            return;
          }
          if (opts.keepRedactedNotifications) {
            const m = c.value as MutationEnvelope;
            await req(
              t.objectStore('notifications').add({
                mutation_id: m.mutation_id,
                site_id,
                reason: opts.reason || 'SITE_REVOKED',
                at: Date.now(),
              } as never),
            );
          }
          await req(c.delete());
          c.continue();
        };
        cur.onerror = () => rej(cur.error);
      });
      // entities: range on compound key (site_id, ..., ...)
      const entsRange = IDBKeyRange.bound([site_id], [site_id, '￿', '￿']);
      const eCur = t.objectStore('entities').openCursor(entsRange);
      await new Promise<void>((res, rej) => {
        eCur.onsuccess = async () => {
          const c = eCur.result;
          if (!c) {
            res();
            return;
          }
          await req(c.delete());
          c.continue();
        };
        eCur.onerror = () => rej(eCur.error);
      });
      await req(t.objectStore('checkpoints').delete(site_id));
      // conflicts: linear scan
      const cCur = t.objectStore('conflicts').openCursor();
      await new Promise<void>((res, rej) => {
        cCur.onsuccess = async () => {
          const c = cCur.result;
          if (!c) {
            res();
            return;
          }
          const v = c.value as { envelope?: { site_id?: string } };
          if (v.envelope?.site_id === site_id) await req(c.delete());
          c.continue();
        };
        cCur.onerror = () => rej(cCur.error);
      });
      await new Promise<void>((res, rej) => {
        t.oncomplete = () => res();
        t.onerror = () => rej(t.error);
      });
    },
  };
}
